import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import XGSIcon from "../icon/xgsIcon";
import XGSIcons from "../icon/xgsIcons";
import Loading from "../loading/loading";
import {
  loadFloatingSidebarState,
  saveFloatingSidebarState,
} from "./floatingSlideOutSidebarStorage";
import "./floatingSlideOutSidebar.scss";

export type FloatingSlideOutSidebarSize =
  | "extrasmall"
  | "small"
  | "small-500"
  | "small-605"
  | "medium"
  | "medium-860"
  | "large";

const SIZE_DEFAULT_WIDTH: Record<FloatingSlideOutSidebarSize, number> = {
  extrasmall: 380,
  small: 460,
  "small-500": 500,
  "small-605": 605,
  medium: 820,
  "medium-860": 860,
  large: 1260,
};

export interface FloatingSlideOutSidebarProps {
  /** Unique id for this sidebar instance; used for localStorage so layout is remembered per usage. */
  storageKey: string;
  header: string;
  onClose?: () => void;
  show: boolean;
  spinner?: boolean;
  size?: FloatingSlideOutSidebarSize;
  className?: string;
  /** When false, position/size are not read or written to localStorage. */
  persist?: boolean;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  children?: React.ReactNode;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function getViewportSize(): { w: number; h: number } {
  if (typeof window === "undefined") return { w: 1200, h: 800 };
  return { w: window.innerWidth, h: window.innerHeight };
}

function getInitialBox(
  storageKey: string,
  size: FloatingSlideOutSidebarSize,
  persist: boolean,
  minWidth: number,
  minHeight: number,
  maxWidthProp: number | undefined,
  maxHeightProp: number | undefined
): { left: number; top: number; width: number; height: number } {
  const { w, h } = getViewportSize();
  const maxW = Math.min(maxWidthProp ?? w * 0.95, w);
  const maxH = Math.min(maxHeightProp ?? h * 0.95, h);
  const defaultWidth = SIZE_DEFAULT_WIDTH[size] ?? SIZE_DEFAULT_WIDTH.medium;

  if (persist && typeof window !== "undefined") {
    const saved = loadFloatingSidebarState(storageKey);
    if (saved) {
      return {
        left: clamp(saved.left, 0, Math.max(0, w - minWidth)),
        top: clamp(saved.top, 0, Math.max(0, h - minHeight)),
        width: clamp(saved.width, minWidth, maxW),
        height: clamp(saved.height, minHeight, maxH),
      };
    }
  }
  const width = clamp(defaultWidth, minWidth, maxW);
  const height = clamp(h * 0.85, minHeight, maxH);
  return { left: w - width, top: Math.max(0, h - height), width, height };
}

const FloatingSlideOutSidebar: React.FC<FloatingSlideOutSidebarProps> = memo(
  ({
    storageKey,
    header,
    onClose,
    show,
    spinner,
    size = "medium",
    className,
    persist = true,
    minWidth: minWidthProp,
    minHeight: minHeightProp,
    maxWidth: maxWidthProp,
    maxHeight: maxHeightProp,
    children,
  }) => {
    const minWidth = minWidthProp ?? 280;
    const minHeight = minHeightProp ?? 200;

    const [box, setBox] = useState(() =>
      getInitialBox(storageKey, size, persist, minWidth, minHeight, maxWidthProp, maxHeightProp)
    );

    const prevStorageKeyRef = useRef(storageKey);
    useEffect(() => {
      if (prevStorageKeyRef.current === storageKey) return;
      prevStorageKeyRef.current = storageKey;
      setBox(
        getInitialBox(storageKey, size, persist, minWidth, minHeight, maxWidthProp, maxHeightProp)
      );
    }, [storageKey, size, persist, minWidth, minHeight, maxWidthProp, maxHeightProp]);

    const persistBox = useCallback(
      (next: typeof box) => {
        if (!persist) return;
        saveFloatingSidebarState(storageKey, next);
      },
      [persist, storageKey]
    );

    const dragStart = useRef<{ x: number; y: number; left: number; top: number } | null>(null);

    const onHeaderPointerDown = useCallback(
      (e: React.PointerEvent) => {
        if (e.button !== 0) return;
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        dragStart.current = {
          x: e.clientX,
          y: e.clientY,
          left: box.left,
          top: box.top,
        };
      },
      [box.left, box.top]
    );

    const onHeaderPointerMove = useCallback(
      (e: React.PointerEvent) => {
        if (!dragStart.current) return;
        const { w, h } = getViewportSize();
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        const nextLeft = clamp(
          dragStart.current.left + dx,
          0,
          Math.max(0, w - box.width)
        );
        const nextTop = clamp(
          dragStart.current.top + dy,
          0,
          Math.max(0, h - box.height)
        );
        setBox((prev) => ({ ...prev, left: nextLeft, top: nextTop }));
      },
      [box.width, box.height]
    );

    const onHeaderPointerUp = useCallback(
      (e: React.PointerEvent) => {
        if (!dragStart.current) return;
        dragStart.current = null;
        (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
        setBox((prev) => {
          persistBox(prev);
          return prev;
        });
      },
      [persistBox]
    );

    const resizeStart = useRef<{
      x: number;
      y: number;
      width: number;
      height: number;
    } | null>(null);

    const onResizePointerDown = useCallback(
      (e: React.PointerEvent) => {
        e.stopPropagation();
        if (e.button !== 0) return;
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        resizeStart.current = {
          x: e.clientX,
          y: e.clientY,
          width: box.width,
          height: box.height,
        };
      },
      [box.width, box.height]
    );

    const onResizePointerMove = useCallback(
      (e: React.PointerEvent) => {
        if (!resizeStart.current) return;
        const { w, h } = getViewportSize();
        const maxW = Math.min(maxWidthProp ?? w * 0.95, w - box.left);
        const maxH = Math.min(maxHeightProp ?? h * 0.95, h - box.top);
        const dx = e.clientX - resizeStart.current.x;
        const dy = e.clientY - resizeStart.current.y;
        const nextW = clamp(resizeStart.current.width + dx, minWidth, Math.max(minWidth, maxW));
        const nextH = clamp(resizeStart.current.height + dy, minHeight, Math.max(minHeight, maxH));
        setBox((prev) => ({ ...prev, width: nextW, height: nextH }));
      },
      [box.left, box.top, minWidth, minHeight, maxWidthProp, maxHeightProp]
    );

    const onResizePointerUp = useCallback(
      (e: React.PointerEvent) => {
        if (!resizeStart.current) return;
        resizeStart.current = null;
        (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
        setBox((prev) => {
          persistBox(prev);
          return prev;
        });
      },
      [persistBox]
    );

    useEffect(() => {
      const onWinResize = () => {
        const { w, h } = getViewportSize();
        const maxW = Math.min(maxWidthProp ?? w * 0.95, w);
        const maxH = Math.min(maxHeightProp ?? h * 0.95, h);
        setBox((prev) => {
          const width = clamp(prev.width, minWidth, maxW);
          const height = clamp(prev.height, minHeight, maxH);
          const left = clamp(prev.left, 0, Math.max(0, w - width));
          const top = clamp(prev.top, 0, Math.max(0, h - height));
          const next = { left, top, width, height };
          if (persist) saveFloatingSidebarState(storageKey, next);
          return next;
        });
      };
      window.addEventListener("resize", onWinResize);
      return () => window.removeEventListener("resize", onWinResize);
    }, [maxWidthProp, maxHeightProp, minWidth, minHeight, persist, storageKey]);

    const style = useMemo(
      () =>
        ({
          left: box.left,
          top: box.top,
          width: box.width,
          height: box.height,
        } as React.CSSProperties),
      [box]
    );

    const stopDrag = useCallback((e: React.PointerEvent) => {
      e.stopPropagation();
    }, []);

    return (
      <div
        className={`
          xgs-floating-slide-out-sidebar
          ${show ? "" : " xgs-floating-slide-out-sidebar--closed"}
          ${className ? ` ${className}` : ""}`}
        style={style}
      >
        <div
          className="xgs-floating-slide-out-sidebar__header"
          onPointerDown={onHeaderPointerDown}
          onPointerMove={onHeaderPointerMove}
          onPointerUp={onHeaderPointerUp}
          onPointerCancel={onHeaderPointerUp}
        >
          <div className="xgs-floating-slide-out-sidebar__header-title">
            {header}
            {spinner && (
              <span className="xgs-floating-slide-out-sidebar__spinner">
                <Loading isLoading={true} />
              </span>
            )}
          </div>
          <div className="xgs-floating-slide-out-sidebar__close" onPointerDown={stopDrag}>
            <XGSIcon icon={XGSIcons.faTimes} size="lg" onClick={onClose && onClose} />
          </div>
        </div>
        <div className="xgs-floating-slide-out-sidebar__content">{children}</div>
        <div
          className="xgs-floating-slide-out-sidebar__resize"
          onPointerDown={onResizePointerDown}
          onPointerMove={onResizePointerMove}
          onPointerUp={onResizePointerUp}
          onPointerCancel={onResizePointerUp}
          aria-hidden
        />
      </div>
    );
  }
);

export default FloatingSlideOutSidebar;
